import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  CircularProgress,
  Pagination,
} from "@mui/material";
import {
  FaPlus,
  FaCheck,
  FaEdit,
  FaTrash,
  FaPen,
  FaRobot,
  FaUser,
  FaMagic,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
} from "react-icons/fa";
import CreateObjectiveQuestion from "../../pages/CreateObjectiveQuestionAI";
import { useState, useEffect, useRef } from "react";
import DeleteDialog from "../dialog/delete";

// Reusable component for Text/Math toggle input
// ToggleInput component
// ── ToggleInput ──────────────────────────────────────────────────────────────
const ToggleInput = ({
  value,
  onChange,
  mode,
  placeholder,
  refProp,
  minHeight,
}) => {
  // 1. Define the shared styles once
  const sharedStyles = {
    width: "100%",
    minHeight: minHeight || 100,
    padding: "10px 14px",
    borderRadius: 8,
    border: "1.5px solid rgba(2,136,209,0.25)",
    fontSize: 15,
    fontFamily: "inherit",
    color: "#0c1a2e",
    background: "#fff",
    resize: "vertical",
    outline: "none",
    transition: "border-color 0.2s",
  };

  return mode === "text" ? (
    <textarea
      ref={refProp}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      // 2. Spread the object here
      style={sharedStyles}
      onFocus={(e) => (e.target.style.borderColor = "#0288D1")}
      onBlur={(e) => (e.target.style.borderColor = "rgba(2,136,209,0.25)")}
    />
  ) : (
    <math-field
      ref={refProp}
      value={value}
      onInput={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      default-mode="text"
      smart-fence
      // 3. Spread the object here too (overriding minHeight if needed)
      style={{ ...sharedStyles, minHeight: minHeight || 50 }}
    />
  );
};
// ── OptionInput ──────────────────────────────────────────────────────────────
const OptionInput = ({
  opt,
  value,
  refProp,
  mode,
  onChange,
  // setOptMode,
  // Optmode,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "#334155",
        textTransform: "uppercase",
        letterSpacing: "0.6px",
        mb: 0.75,
      }}
    >
      Option {opt.toUpperCase()}
    </Typography>
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ flex: 1 }}>
        <ToggleInput
          value={value}
          onChange={onChange}
          mode={mode}
          placeholder={`Type option ${opt.toUpperCase()}…`}
          refProp={refProp}
        />
      </Box>
    </Stack>
  </Box>
);

const ObjectiveSection = ({
  QUESTION_CHOICES,
  subId,
  tokenString,

  subjectName,
  numObjQuestions,
  setNumObjQuestions,
  showObjectiveGen,
  toggleObjective,
  handleAutoExamObjective,
  loadingautobj,
  handleObjSubmit,
  objImagePreview,
  editingObj,
  isRemovingBg,
  OBJmode,
  setOBJMode,
  Optmode,
  setOptMode,
  objForm,
  setObjForm,
  questionRef,
  optionRefs,
  loadingobj,
  instObjs,
  expandedObjId,
  setExpandedObjId,
  editDisabledId,
  setEditDisabledId,
  renderContent,
  setEditingObj,
  deleteOpen,
  setDeleteOpen,
  confirmDelete,
  setDeleteId,
  setDeleteType,
  deleteLoading,
  setInstObjs,
  // isRestrictedSubject,
}) => {
  const [objPage, setObjPage] = useState(1);
  const OBJ_PER_PAGE = 5;
  const editFormRef = useRef(null);

  const handleObjPageChange = (e, value) => setObjPage(value);

  const objStart = (objPage - 1) * OBJ_PER_PAGE;
  const objEnd = objStart + OBJ_PER_PAGE;
  const paginatedObjs = instObjs.slice(objStart, objEnd);

  // Ensure current page is valid
  useEffect(() => {
    const totalPages = Math.ceil(instObjs.length / OBJ_PER_PAGE) || 1;
    setObjPage((currentPage) =>
      currentPage > totalPages ? totalPages : currentPage
    );
  }, [instObjs]);

  const containsMath = (text = "") => {
    const mathPatterns = [
      "\\frac",
      "\\sqrt",
      "\\sum",
      "\\int",
      "^",
      "\\(",
      "\\)",
    ];

    return mathPatterns.some((pattern) => text.includes(pattern));
  };

  const formatQuestionText = (text = "") => {
    if (!text) return "";

    // Add spaces between lowercase and uppercase letters
    let formatted = text.replace(/([a-z])([A-Z])/g, "$1 $2");

    // Add spaces before LaTeX commands like \sqrt
    formatted = formatted.replace(
      /([a-zA-Z])\\(sqrt|frac|sum|int)/g,
      "$1 \\\\$2"
    );

    // Add space before question mark
    formatted = formatted.replace(/\?/g, " ?");

    return formatted;
  };

  // ── Main Return ──────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Top Controls ──────────────────────────────────────────────────── */}
      <Stack
        direction={"column"}
        alignItems={"flex-start"}
        justifyContent="space-between"
        mb={3}
        gap={1.5}
      >
        <Button
          onClick={toggleObjective}
          variant="contained"
          startIcon={
            showObjectiveGen ? <FaUser size={12} /> : <FaRobot size={12} />
          }
          sx={{
            borderRadius: 99,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.78rem",
            px: 2.5,
            background: showObjectiveGen
              ? "linear-gradient(135deg, #7c3aed, #a855f7)"
              : "linear-gradient(135deg, #0288D1, #0ea5e9)",
            boxShadow: showObjectiveGen
              ? "0 4px 12px rgba(124,58,237,0.3)"
              : "0 4px 12px rgba(2,136,209,0.3)",
            "&:hover": {
              background: showObjectiveGen
                ? "linear-gradient(135deg, #6d28d9, #7c3aed)"
                : "linear-gradient(135deg, #0277bd, #0288D1)",
            },
          }}
        >
          {showObjectiveGen ? "Switch to Manual Mode" : "Switch to AI Mode"}
        </Button>
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: "#0c1a2e", letterSpacing: "-0.3px" }}
          >
            Objective Questions
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748b" }}>
            {instObjs.length} question{instObjs.length !== 1 ? "s" : ""} added
          </Typography>
        </Box>
      </Stack>

      {/* ── Auto Generate Row ──────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          background: "rgba(2,136,209,0.04)",
          border: "1px solid rgba(2,136,209,0.14)",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#0288D1",
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            mb: 1.5,
          }}
        >
          🤖 Auto Generate
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
        >
          <TextField
            type="number"
            placeholder="No. of questions"
            value={numObjQuestions}
            onChange={(e) => setNumObjQuestions(Number(e.target.value))}
            size="small"
            sx={{
              width: { xs: "100%", sm: 200 },
              "& .MuiOutlinedInput-root": {
                color: "#0c1a2e",
                borderRadius: 2,
                background: "#fff",
                "& fieldset": { borderColor: "rgba(2,136,209,0.25)" },
                "&:hover fieldset": { borderColor: "#0288D1" },
                "&.Mui-focused fieldset": { borderColor: "#0288D1" },
              },
              "& input::placeholder": { color: "#94a3b8", fontSize: "0.85rem" },
            }}
          />
          <Button
            onClick={handleAutoExamObjective}
            disabled={loadingautobj || !numObjQuestions}
            variant="contained"
            startIcon={loadingautobj ? null : <FaMagic size={12} />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              background:
                loadingautobj || !numObjQuestions
                  ? "#e2e8f0"
                  : "linear-gradient(135deg, #0288D1, #0ea5e9)",
              color: loadingautobj || !numObjQuestions ? "#94a3b8" : "#fff",
              boxShadow:
                loadingautobj || !numObjQuestions
                  ? "none"
                  : "0 4px 12px rgba(2,136,209,0.25)",
              "&:hover": {
                background:
                  loadingautobj || !numObjQuestions
                    ? "#e2e8f0"
                    : "linear-gradient(135deg, #0277bd, #0288D1)",
              },
              "&:disabled": { color: "#94a3b8", background: "#e2e8f0" },
            }}
          >
            {loadingautobj ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={14} sx={{ color: "#94a3b8" }} />
                <span>Generating…</span>
              </Stack>
            ) : (
              "Auto Generate"
            )}
          </Button>
        </Stack>
      </Paper>

      {/* ── AI Mode ────────────────────────────────────────────────────────── */}
      {showObjectiveGen && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            background: "rgba(124,58,237,0.04)",
            border: "1px solid rgba(124,58,237,0.16)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: "1px solid rgba(124,58,237,0.1)",
              background: "rgba(124,58,237,0.06)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FaRobot size={14} color="#7c3aed" />
              <Typography
                sx={{ fontWeight: 700, color: "#7c3aed", fontSize: "0.82rem" }}
              >
                AI Question Generator
              </Typography>
            </Stack>
          </Box>
          <Box sx={{ p: 2.5 }}>
            <CreateObjectiveQuestion
              token={tokenString}
              subjectId={subId}
              subjectName={subjectName}
              setInstObjs={setInstObjs}
            />
          </Box>
        </Paper>
      )}

      {/* ── Manual Form ────────────────────────────────────────────────────── */}
      {!showObjectiveGen && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            background: "#fff",
            border: "1px solid rgba(2,136,209,0.15)",
            boxShadow: "0 2px 12px rgba(2,136,209,0.06)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: "1px solid #e2e8f0",
              background:
                "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.02))",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FaPen size={13} color="#0288D1" />
              <Typography
                sx={{ fontWeight: 700, color: "#0c1a2e", fontSize: "0.88rem" }}
              >
                {editingObj ? "Edit Question" : "Add Question Manually"}
              </Typography>
            </Stack>
          </Box>

          <Box
            component="form"
            onSubmit={handleObjSubmit}
            ref={editFormRef}
            sx={{ p: 2.5 }}
          >
            <Stack spacing={2.5}>
              {/* Image preview */}
              {(objImagePreview || editingObj?.image_url) && (
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    p: 1.5,
                    textAlign: "center",
                    position: "relative",
                  }}
                >
                  {isRemovingBg && (
                    <Stack alignItems="center" spacing={1} py={2}>
                      <CircularProgress size={20} sx={{ color: "#0288D1" }} />
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Removing background…
                      </Typography>
                    </Stack>
                  )}
                  <img
                    src={objImagePreview || editingObj?.image_url}
                    alt="Question preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      borderRadius: 8,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}

              {/* Question */}
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={0.75}
                >
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "#334155",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    Question
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                   
                  </Stack>
                </Stack>

                {OBJmode === "text" && (
                  <ToggleInput
                    mode="text"
                    refProp={questionRef}
                    value={objForm.question_text}
                    onChange={(val) =>
                      setObjForm({ ...objForm, question_text: val })
                    }
                    placeholder="Type your question here…"
                    minHeight={120}
                  />
                )}
              </Box>

              {/* Options */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#334155",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    mb: 1.5,
                  }}
                >
                  Answer Options
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {["a", "b", "c", "d"].map((opt) => (
                    <OptionInput
                      key={opt}
                      opt={opt}
                      value={objForm[`option_${opt}`]}
                      refProp={optionRefs[opt]}
                      mode={Optmode}
                      onChange={(val) =>
                        setObjForm({ ...objForm, [`option_${opt}`]: val })
                      }
                      setOptMode={setOptMode}
                      Optmode={Optmode}
                    />
                  ))}
                </Paper>
              </Box>

              {/* Correct Answer */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#334155",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    mb: 0.75,
                  }}
                >
                  Correct Answer
                </Typography>
                <Select
                  value={objForm.correct_answer}
                  onChange={(e) =>
                    setObjForm({ ...objForm, correct_answer: e.target.value })
                  }
                  required
                  size="small"
                  displayEmpty
                  sx={{
                    width: 180,
                    color: "#0c1a2e",
                    borderRadius: 2,
                    background: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(2,136,209,0.25)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0288D1",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0288D1",
                    },
                    "& .MuiSvgIcon-root": { color: "#0288D1" },
                  }}
                >
                  <MenuItem value="" disabled sx={{ color: "#94a3b8" }}>
                    Select Answer
                  </MenuItem>
                  {QUESTION_CHOICES.map((choice) => (
                    <MenuItem key={choice} value={choice}>
                      {choice}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Submit */}
              <Button
                type="submit"
                variant="contained"
                disabled={loadingobj}
                startIcon={
                  loadingobj ? null : editingObj ? (
                    <FaCheck size={12} />
                  ) : (
                    <FaPlus size={12} />
                  )
                }
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  alignSelf: "flex-start",
                  px: 3,
                  background: "linear-gradient(135deg, #059669, #10b981)",
                  boxShadow: "0 4px 12px rgba(5,150,105,0.25)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #047857, #059669)",
                  },
                  "&:disabled": { opacity: 0.5 },
                }}
              >
                {loadingobj ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={14} sx={{ color: "#fff" }} />
                    <span>{editingObj ? "Updating…" : "Saving…"}</span>
                  </Stack>
                ) : editingObj ? (
                  "Update Question"
                ) : (
                  "Save Question"
                )}
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {/* ── Questions List ──────────────────────────────────────────────────── */}
      <Stack spacing={1.5}>
        {paginatedObjs.map((item, i) => {
          const isExpanded = expandedObjId === item.id;
          const isEditingItem = editDisabledId === item.id;
          const hasCalculation = containsMath(item.question_text);

          return (
            <Paper
              key={item.id}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: isEditingItem
                  ? "1px solid rgba(2,136,209,0.4)"
                  : "1px solid #e2e8f0",
                background: isEditingItem ? "rgba(2,136,209,0.02)" : "#f8fafc",
                overflow: "hidden",
                transition: "all 0.2s",
                boxShadow: isEditingItem
                  ? "0 4px 16px rgba(2,136,209,0.1)"
                  : "none",
              }}
            >
              {/* Question header */}
              <Box sx={{ p: 2 }}>
                {/* Image or SVG */}
                {item.image_url ? (
                  <Box
                    sx={{
                      mb: 1.5,
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid #e2e8f0",
                      textAlign: "center",
                      background: "#fff",
                      p: 1,
                    }}
                  >
                    <img
                      src={item.image_url}
                      alt="Question"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 180,
                        objectFit: "contain",
                        borderRadius: 6,
                      }}
                    />
                  </Box>
                ) : item.svg_code ? (
                  <Box
                    sx={{ mb: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: item.svg_code }}
                  />
                ) : null}

                {/* Question text */}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        color: "#fff",
                      }}
                    >
                      {objStart + i + 1}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      color: "#0c1a2e",
                      fontSize: "0.9rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {renderContent(formatQuestionText(item.question_text))}
                  </Box>
                </Stack>

                {/* Expanded options */}
                {isExpanded && (
                  <Box
                    sx={{
                      mt: 2,
                      pl: 5,
                      pt: 2,
                      borderTop: "1px solid #e2e8f0",
                    }}
                  >
                    <Stack spacing={1} mb={1.5}>
                      {["a", "b", "c", "d"].map((key) => (
                        <Stack
                          key={key}
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <Chip
                            label={key.toUpperCase()}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 800,
                              flexShrink: 0,
                              background:
                                item.correct_answer?.toLowerCase() === key
                                  ? "rgba(5,150,105,0.12)"
                                  : "rgba(2,136,209,0.08)",
                              color:
                                item.correct_answer?.toLowerCase() === key
                                  ? "#059669"
                                  : "#0288D1",
                              border: `1px solid ${
                                item.correct_answer?.toLowerCase() === key
                                  ? "rgba(5,150,105,0.25)"
                                  : "rgba(2,136,209,0.2)"
                              }`,
                            }}
                          />
                          <Box
                            sx={{
                              fontSize: "0.85rem",
                              color: "#334155",
                              lineHeight: 1.5,
                            }}
                          >
                            {renderContent(item[`option_${key}`])}
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Answer:
                      </Typography>
                      <Chip
                        label={item.correct_answer}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.72rem",
                          fontWeight: 800,
                          background: "rgba(5,150,105,0.12)",
                          color: "#059669",
                          border: "1px solid rgba(5,150,105,0.25)",
                        }}
                      />
                    </Stack>
                  </Box>
                )}
              </Box>

              {/* Actions */}
              <Box
                sx={{
                  px: 2,
                  py: 1.2,
                  borderTop: "1px solid #e2e8f0",
                  background: "#fff",
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  flexWrap="wrap"
                  gap={0.5}
                >
                  <Button
                    size="small"
                    onClick={() =>
                      setExpandedObjId(isExpanded ? null : item.id)
                    }
                    startIcon={
                      isExpanded ? (
                        <FaChevronUp size={10} />
                      ) : (
                        <FaChevronDown size={10} />
                      )
                    }
                    sx={{
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: "#0288D1",
                      borderColor: "rgba(2,136,209,0.25)",
                      border: "1px solid rgba(2,136,209,0.25)",
                      px: 1.5,
                      "&:hover": { background: "rgba(2,136,209,0.06)" },
                    }}
                  >
                    {isExpanded ? "See less" : "See more"}
                  </Button>

                  <Tooltip
                    title={
                      hasCalculation
                        ? "Editing disabled — contains math notation"
                        : "Edit question"
                    }
                    arrow
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={isEditingItem || hasCalculation}
                        onClick={() => {
                          if (hasCalculation) return;
                          setEditDisabledId(item.id);
                          setEditingObj(item);
                          setObjForm(item);
                          if (showObjectiveGen) toggleObjective();
                          setTimeout(() => {
                            editFormRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          }, 200);
                        }}
                        sx={{
                          color: hasCalculation ? "#cbd5e1" : "#0288D1",
                          background: hasCalculation
                            ? "#f8fafc"
                            : "rgba(2,136,209,0.08)",
                          border: `1px solid ${
                            hasCalculation ? "#e2e8f0" : "rgba(2,136,209,0.2)"
                          }`,
                          borderRadius: 1.5,
                          "&:hover": {
                            background: hasCalculation
                              ? "#f8fafc"
                              : "rgba(2,136,209,0.16)",
                          },
                          "&:disabled": { opacity: 0.4 },
                        }}
                      >
                        {isEditingItem ? (
                          <CircularProgress
                            size={12}
                            sx={{ color: "#0288D1" }}
                          />
                        ) : (
                          <FaEdit size={13} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Delete question" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeleteId(item.id);
                        setDeleteType("instobj");
                        setDeleteOpen(true);
                      }}
                      sx={{
                        color: "#ef4444",
                        background: "rgba(239,68,68,0.08)",
                        border: "1px solid rgba(239,68,68,0.2)",
                        borderRadius: 1.5,
                        "&:hover": { background: "rgba(239,68,68,0.16)" },
                      }}
                    >
                      <FaTrash size={13} />
                    </IconButton>
                  </Tooltip>

                  {hasCalculation && (
                    <Chip
                      label="Math — Edit Disabled"
                      size="small"
                      icon={
                        <FaExclamationTriangle
                          size={9}
                          style={{ marginLeft: 8, color: "#f59e0b" }}
                        />
                      }
                      sx={{
                        height: 20,
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        background: "rgba(245,158,11,0.1)",
                        color: "#b45309",
                        border: "1px solid rgba(245,158,11,0.25)",
                      }}
                    />
                  )}

                  {isEditingItem && (
                    <Chip
                      label="Currently Editing"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        background: "rgba(2,136,209,0.1)",
                        color: "#0288D1",
                        border: "1px solid rgba(2,136,209,0.25)",
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Paper>
          );
        })}
      </Stack>

      {/* ── Pagination ──────────────────────────────────────────────────────── */}
      {instObjs.length > 0 && (
        <Stack alignItems="center" sx={{ mt: 3, mb: 2 }}>
          <Pagination
            count={Math.ceil(instObjs.length / OBJ_PER_PAGE)}
            page={objPage}
            onChange={handleObjPageChange}
            color="primary"
            shape="rounded"
            size="large"
            sx={{
              "& .MuiPaginationItem-root": {
                fontWeight: 700,
                borderRadius: 2,
                color: "#64748b",
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                color: "#fff",
                boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
              },
            }}
          />
        </Stack>
      )}

      {/* ── Delete Dialog ───────────────────────────────────────────────────── */}
      <DeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        deleteLoading={deleteLoading}
        title="question"
      />
    </Box>
  );
};

export default ObjectiveSection;
